// Supervisor "Team Academic Breaks" page — read-only roster of every
// employee and supervisor that has logged breaks, grouped by person with
// a mini calendar (same UX as the admin page, but inside SupervisorLayout
// and hitting /api/supervisor/academic-breaks/all).
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Calendar as DayPickerCalendar } from '../../components/ui/calendar';
import {
  GraduationCap,
  Search,
  Calendar,
  Users,
  Filter,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  UserCheck,
  UserX,
  Clock as ClockIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays, differenceInMonths, eachDayOfInterval } from 'date-fns';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDuration = (s, e) => {
  try {
    const start = parseISO(s);
    const end = parseISO(e);
    const months = differenceInMonths(end, start);
    const days = differenceInDays(end, start) - months * 30;
    if (months > 0 && days > 0) return `${months}mo ${days}d`;
    if (months > 0) return `${months} month${months !== 1 ? 's' : ''}`;
    const total = differenceInDays(end, start) + 1;
    return `${total} day${total !== 1 ? 's' : ''}`;
  } catch {
    return '';
  }
};

const STATUS_STYLES = {
  current: { label: 'On Break Now', class: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
  upcoming: { label: 'Upcoming', class: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  past: { label: 'Past', class: 'bg-slate-500/20 text-slate-400 border-slate-500/40' },
};

const STATUS_RANK = { current: 0, upcoming: 1, past: 2 };

export default function SupervisorTeamAcademicBreaksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [pendingUsers, setPendingUsers] = useState([]);
  const [completion, setCompletion] = useState({ total: 0, filled: 0, pending: 0 });
  const [pendingOpen, setPendingOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/supervisor/academic-breaks/all`);
      setItems(res.data?.items || []);
      setPendingUsers(res.data?.pending_users || []);
      setCompletion({
        total: res.data?.total_active_staff || 0,
        filled: res.data?.filled_count || 0,
        pending: res.data?.pending_count || 0,
      });
    } catch (_e) {
      toast.error('Failed to load academic breaks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const counts = useMemo(() => ({
    all: items.length,
    current: items.filter((i) => i.status === 'current').length,
    upcoming: items.filter((i) => i.status === 'upcoming').length,
    past: items.filter((i) => i.status === 'past').length,
  }), [items]);

  const groupedAlphabetical = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byUser = new Map();
    for (const it of items) {
      if (statusFilter !== 'all' && it.status !== statusFilter) continue;
      const existing = byUser.get(it.user_id) || {
        user_id: it.user_id,
        user_name: it.user_name,
        user_email: it.user_email,
        user_role: it.user_role,
        breaks: [],
        best_status: 'past',
      };
      existing.breaks.push(it);
      if (STATUS_RANK[it.status] < STATUS_RANK[existing.best_status]) {
        existing.best_status = it.status;
      }
      byUser.set(it.user_id, existing);
    }
    let users = Array.from(byUser.values());
    if (q) {
      users = users.filter(u =>
        (u.user_name || '').toLowerCase().includes(q) ||
        (u.user_email || '').toLowerCase().includes(q)
      );
    }
    for (const u of users) {
      u.breaks.sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));
    }
    users.sort((a, b) =>
      (a.user_name || '').localeCompare(b.user_name || '', undefined, { sensitivity: 'base' })
    );
    return users;
  }, [items, search, statusFilter]);

  const toggle = (uid) => setExpanded(prev => ({ ...prev, [uid]: !prev[uid] }));

  return (
    <SupervisorLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        <div className="border-b border-border pb-6">
          <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <GraduationCap className="w-6 h-6 text-primary" strokeWidth={1.5} />
            TEAM ACADEMIC BREAKS
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only overview of all submitted academic breaks — employees, other supervisors and yourself.
            <br />To add or edit your own breaks, use <strong>My Academic Breaks</strong>.
          </p>
        </div>

        {/* Completion overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-border bg-card p-3 rounded-sm">
            <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> Active staff
            </p>
            <p className="text-2xl font-barlow font-bold mt-1">{completion.total}</p>
          </div>
          <div className="border border-green-500/40 bg-green-500/5 p-3 rounded-sm">
            <p className="text-xs uppercase tracking-wider text-green-300 flex items-center gap-2">
              <UserCheck className="w-3.5 h-3.5" /> Filled
            </p>
            <p className="text-2xl font-barlow font-bold mt-1 text-green-300">
              {completion.filled}
              <span className="text-xs ml-2 text-muted-foreground">
                ({completion.total ? Math.round((completion.filled / completion.total) * 100) : 0}%)
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPendingOpen((o) => !o)}
            className="border border-amber-500/40 bg-amber-500/5 p-3 rounded-sm text-left hover:bg-amber-500/10 transition-colors"
            data-testid="sup-pending-toggle"
          >
            <p className="text-xs uppercase tracking-wider text-amber-300 flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><UserX className="w-3.5 h-3.5" /> Pending (not filled)</span>
              {pendingOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </p>
            <p className="text-2xl font-barlow font-bold mt-1 text-amber-300">{completion.pending}</p>
          </button>
        </div>

        {pendingOpen && (
          <Card className="bg-card border-amber-500/40 rounded-sm" data-testid="sup-pending-list">
            <CardContent className="p-3 sm:p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                {pendingUsers.length} staff member{pendingUsers.length !== 1 ? 's' : ''} have not submitted any academic break yet
              </p>
              {pendingUsers.length === 0 ? (
                <p className="text-sm text-green-400">🎉 Everyone has submitted their breaks.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {pendingUsers.map((u) => (
                    <div key={u.id} className="flex items-center gap-2 text-sm p-1.5 bg-background/40 border border-border/60 rounded-sm">
                      <UserX className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate font-medium">{u.name}</p>
                        <p className="truncate text-[10px] text-muted-foreground">
                          {u.role === 'supervisor' ? 'Supervisor' : 'Employee'}
                          {u.email && <> • {u.email}</>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: 'all', label: 'Total', count: counts.all, class: 'border-border' },
            { key: 'current', label: 'On Break Now', count: counts.current, class: 'border-amber-500/40' },
            { key: 'upcoming', label: 'Upcoming', count: counts.upcoming, class: 'border-blue-500/40' },
            { key: 'past', label: 'Past', count: counts.past, class: 'border-slate-500/40' },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              className={cn(
                'p-3 rounded-sm border bg-card text-left transition-colors',
                s.class,
                statusFilter === s.key && 'ring-2 ring-primary/40'
              )}
              data-testid={`sup-stat-${s.key}`}
            >
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
              <p className="text-2xl font-barlow font-bold mt-1">{s.count}</p>
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="pl-10 bg-background border-border rounded-sm"
              data-testid="sup-search-input"
            />
          </div>
          {statusFilter !== 'all' && (
            <Button variant="outline" onClick={() => setStatusFilter('all')} className="border-border rounded-sm">
              <Filter className="w-4 h-4 mr-2" />
              Clear Filter ({STATUS_STYLES[statusFilter]?.label || statusFilter})
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground p-4">Loading…</p>
        ) : groupedAlphabetical.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center space-y-2">
              <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto opacity-50" />
              <p className="text-sm text-muted-foreground">
                {search || statusFilter !== 'all' ? 'No results match your filters.' : 'No academic breaks submitted yet.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {groupedAlphabetical.map((u) => (
              <EmployeeBreakCard
                key={u.user_id}
                user={u}
                expanded={!!expanded[u.user_id]}
                onToggle={() => toggle(u.user_id)}
              />
            ))}
          </div>
        )}
      </div>
    </SupervisorLayout>
  );
}

function EmployeeBreakCard({ user, expanded, onToggle }) {
  const summarySty = STATUS_STYLES[user.best_status] || STATUS_STYLES.past;
  return (
    <Card className="bg-card border-border rounded-sm hover:border-primary/30 transition-colors" data-testid={`sup-employee-row-${user.user_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3 sm:p-4 flex items-center gap-3"
        data-testid={`sup-employee-toggle-${user.user_id}`}
      >
        <Users className="w-4 h-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{user.user_name}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {user.user_role === 'supervisor' ? 'Supervisor' : 'Employee'}
            {user.user_email && <span> • {user.user_email}</span>}
          </p>
        </div>
        <span className="text-[11px] text-muted-foreground hidden sm:inline">
          {user.breaks.length} break{user.breaks.length !== 1 ? 's' : ''}
        </span>
        {user.best_status === 'current' && (
          <span className={cn('text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm', summarySty.class)}>
            {summarySty.label}
          </span>
        )}
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <CardContent className="px-3 sm:px-4 pb-4 pt-0 space-y-3 border-t border-border/60">
          <div className="grid lg:grid-cols-[1fr_auto] gap-4 pt-3">
            <div className="space-y-2">
              {user.breaks.map((b) => {
                const sty = STATUS_STYLES[b.status] || STATUS_STYLES.past;
                return (
                  <div
                    key={b.break_id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2.5 bg-background/40 border border-border/60 rounded-sm"
                    data-testid={`sup-break-row-${b.break_id}`}
                  >
                    <div className="flex items-center gap-2 text-sm font-mono flex-1">
                      <Calendar className="w-4 h-4 text-green-400 shrink-0" />
                      <span>{(() => { try { return format(parseISO(b.start_date), 'dd MMM yyyy'); } catch { return b.start_date; } })()}</span>
                      <span className="text-muted-foreground">→</span>
                      <Calendar className="w-4 h-4 text-red-400 shrink-0" />
                      <span>{(() => { try { return format(parseISO(b.end_date), 'dd MMM yyyy'); } catch { return b.end_date; } })()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-muted-foreground">
                        {formatDuration(b.start_date, b.end_date)}
                      </span>
                      {b.created_at && (
                        <span className="text-[10px] text-muted-foreground/80 flex items-center gap-1" title="Submitted">
                          <ClockIcon className="w-3 h-3" />
                          {(() => {
                            try { return format(parseISO(b.created_at), 'dd MMM yyyy'); }
                            catch { return ''; }
                          })()}
                        </span>
                      )}
                      <span className={cn('text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm', sty.class)}>
                        {sty.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <BreakCalendar breaks={user.breaks} />
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function BreakCalendar({ breaks }) {
  const breakDays = useMemo(() => {
    const days = [];
    for (const b of breaks || []) {
      try {
        const s = parseISO(b.start_date);
        const e = parseISO(b.end_date);
        if (e < s) continue;
        days.push(...eachDayOfInterval({ start: s, end: e }));
      } catch { /* skip */ }
    }
    return days;
  }, [breaks]);

  const defaultMonth = useMemo(() => {
    if (!breaks || breaks.length === 0) return new Date();
    try {
      const today = new Date();
      const current = breaks.find(b => {
        try {
          const s = parseISO(b.start_date);
          const e = parseISO(b.end_date);
          return today >= s && today <= e;
        } catch { return false; }
      });
      if (current) return parseISO(current.start_date);
      return parseISO(breaks[0].start_date);
    } catch {
      return new Date();
    }
  }, [breaks]);

  return (
    <div className="rounded-sm border border-border/60 bg-background/40 p-2" data-testid="sup-break-calendar">
      <div className="flex items-center gap-2 px-2 pt-1 pb-2 text-[11px] text-muted-foreground">
        <CalendarDays className="w-3.5 h-3.5" />
        <span>Days on break highlighted</span>
        <span className="ml-auto inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-green-500/40 border border-green-500/60" />
          Break
        </span>
      </div>
      <DayPickerCalendar
        mode="multiple"
        selected={breakDays}
        defaultMonth={defaultMonth}
        showOutsideDays={false}
        onSelect={() => {}}
        modifiersClassNames={{
          selected: 'bg-green-500/30 text-green-200 hover:bg-green-500/40 rounded-sm',
        }}
        className="p-0"
      />
    </div>
  );
}
